import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, ChevronDown, ChevronRight, Loader2, Upload, X,
  ArrowLeft, ArrowRight, Package,
} from 'lucide-react';
import { formatDateTime } from '../lib/dateFormat';

/**
 * Feb 2026 — Post-Pickup Proof section.
 *
 * Collapsible card that renders ONLY once the parent order is in
 * `picked_up` / `out_for_delivery` / `delivered`. Lets the picker
 * (and manager / floor_incharge / super admin) attach proof photos
 * AND / OR save a numeric pallet quantity AFTER the order has left
 * the dock. Submit button is disabled unless at least one photo is
 * staged OR the pallet-qty input is populated.
 *
 * Props:
 *   - order (required): the order object (reads `id`, `status`).
 *   - API (required): API base URL.
 *   - authHeaders (required): auth headers for axios.
 *   - canEdit (required): true for picker / floor_incharge /
 *     manager / super admin. Read-only mode (no upload UI, no qty
 *     input) when false — the thumbnail grid still renders.
 *   - testidPrefix (optional): default
 *     `post-pickup-proof-section`.
 */
const POST_PICKUP_STATUSES = new Set(['picked_up', 'out_for_delivery', 'delivered']);

const PostPickupProofSection = ({
  order,
  API,
  authHeaders,
  canEdit = false,
  testidPrefix = 'post-pickup-proof-section',
}) => {
  const orderId = order?.id || '';
  const status = (order?.status || '').toLowerCase();
  const unlocked = POST_PICKUP_STATUSES.has(status);

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState({
    photos: [], pallet_qty: null, updated_at: '', updated_by: '',
  });
  const [palletInput, setPalletInput] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]); // File objects
  const [thumbUrls, setThumbUrls] = useState({});
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const fileInputRef = useRef(null);

  const fetchState = useCallback(async () => {
    if (!orderId || !unlocked) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API}/admin/orders/${orderId}/post-pickup`,
        { headers: authHeaders },
      );
      const s = r.data || {};
      setState({
        photos: s.photos || [],
        pallet_qty: s.pallet_qty ?? null,
        updated_at: s.updated_at || '',
        updated_by: s.updated_by || '',
      });
      setPalletInput(
        s.pallet_qty === null || s.pallet_qty === undefined ? '' : String(s.pallet_qty),
      );
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load post-pickup proof.');
    } finally {
      setLoading(false);
    }
  }, [orderId, unlocked, API, authHeaders]);

  useEffect(() => {
    if (expanded) fetchState();
  }, [expanded, fetchState]);

  // Lazy-load thumbnails via authenticated axios → object URLs.
  useEffect(() => {
    if (!expanded || !state.photos.length) return undefined;
    let cancelled = false;
    const created = [];
    (async () => {
      const toFetch = state.photos.filter((p) => !thumbUrls[p.file_id]);
      if (!toFetch.length) return;
      const results = await Promise.all(
        toFetch.map(async (p) => {
          try {
            const r = await axios.get(
              `${API}/admin/orders/${orderId}/post-pickup/photos/${p.file_id}`,
              { responseType: 'blob', headers: authHeaders },
            );
            const url = URL.createObjectURL(r.data);
            created.push(url);
            return [p.file_id, url];
          } catch {
            return [p.file_id, ''];
          }
        }),
      );
      if (cancelled) {
        created.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setThumbUrls((prev) => {
        const next = { ...prev };
        results.forEach(([id, u]) => { if (u) next[id] = u; });
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [expanded, state.photos, orderId, API, authHeaders, thumbUrls]);

  // Revoke blob URLs on unmount.
  useEffect(() => () => {
    Object.values(thumbUrls).forEach((u) => { if (u) URL.revokeObjectURL(u); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickFiles = (ev) => {
    const list = Array.from(ev.target.files || []);
    if (!list.length) return;
    const allowed = list.filter((f) => /^image\//i.test(f.type)
      || /\.(jpe?g|png|webp|heic|heif)$/i.test(f.name || ''));
    setStagedFiles((prev) => [...prev, ...allowed]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeStaged = (idx) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = canEdit && !saving && (
    stagedFiles.length > 0
    || (palletInput !== '' && palletInput !== String(state.pallet_qty ?? ''))
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      stagedFiles.forEach((f) => fd.append('files', f, f.name));
      if (palletInput !== '' && palletInput !== String(state.pallet_qty ?? '')) {
        fd.append('pallet_qty', palletInput);
      }
      await axios.post(
        `${API}/admin/orders/${orderId}/post-pickup/upload`,
        fd,
        {
          headers: {
            ...authHeaders,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setStagedFiles([]);
      await fetchState();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const closeLightbox = useCallback(() => setLightboxIdx(-1), []);
  const nextLightbox = useCallback(
    () => setLightboxIdx((i) => (i < 0 ? -1 : (i + 1) % state.photos.length)),
    [state.photos.length],
  );
  const prevLightbox = useCallback(
    () => setLightboxIdx((i) => (i < 0 ? -1 : (i - 1 + state.photos.length) % state.photos.length)),
    [state.photos.length],
  );
  useEffect(() => {
    if (lightboxIdx < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextLightbox();
      if (e.key === 'ArrowLeft') prevLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, closeLightbox, nextLightbox, prevLightbox]);

  if (!unlocked) return null;

  const photoCount = state.photos.length;
  const palletLabel = state.pallet_qty === null || state.pallet_qty === undefined
    ? '—' : String(state.pallet_qty);

  return (
    <div
      className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden"
      data-testid={`${testidPrefix}-${orderId}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-100/50 transition"
        data-testid={`${testidPrefix}-toggle-${orderId}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-amber-700" />
          : <ChevronRight className="w-4 h-4 text-amber-700" />}
        <Camera className="w-4 h-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">
          Post-Pickup Proof
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-amber-800">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-200">
            <Camera className="w-3 h-3" /> {photoCount} photo{photoCount === 1 ? '' : 's'}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-200">
            <Package className="w-3 h-3" /> Pallets: {palletLabel}
          </span>
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-amber-700">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              data-testid={`${testidPrefix}-error-${orderId}`}
            >
              {error}
            </div>
          )}

          {/* Pallet quantity row */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label
                htmlFor={`${testidPrefix}-pallet-${orderId}`}
                className="block text-[11px] uppercase font-semibold tracking-wider text-amber-800 mb-1"
              >
                Pallet Quantity
              </label>
              <input
                id={`${testidPrefix}-pallet-${orderId}`}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={palletInput}
                onChange={(e) => setPalletInput(e.target.value)}
                disabled={!canEdit || saving}
                placeholder="e.g. 12"
                className="w-32 px-3 py-1.5 rounded-md border border-amber-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:bg-neutral-100"
                data-testid={`${testidPrefix}-pallet-input-${orderId}`}
              />
            </div>

            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-amber-400 bg-white text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  data-testid={`${testidPrefix}-pick-files-${orderId}`}
                >
                  <Upload className="w-4 h-4" /> Add Photos
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  multiple
                  className="hidden"
                  onChange={onPickFiles}
                  data-testid={`${testidPrefix}-file-input-${orderId}`}
                />
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`${testidPrefix}-save-${orderId}`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>

          {/* Staged (not-yet-uploaded) files preview */}
          {stagedFiles.length > 0 && (
            <div className="pt-1">
              <div className="text-[11px] uppercase font-semibold tracking-wider text-amber-800 mb-2">
                Ready to upload ({stagedFiles.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {stagedFiles.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-amber-300 text-xs text-amber-900"
                    data-testid={`${testidPrefix}-staged-${orderId}-${i}`}
                  >
                    <span className="truncate max-w-[16rem]">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeStaged(i)}
                      className="p-0.5 rounded hover:bg-amber-100"
                      aria-label={`Remove ${f.name}`}
                      data-testid={`${testidPrefix}-staged-remove-${orderId}-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded photos grid */}
          {state.photos.length > 0 && (
            <div className="pt-2">
              <div className="text-[11px] uppercase font-semibold tracking-wider text-amber-800 mb-2">
                Uploaded ({state.photos.length})
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {state.photos.map((p, idx) => (
                  <button
                    key={p.file_id}
                    type="button"
                    onClick={() => setLightboxIdx(idx)}
                    className="relative aspect-square rounded-lg overflow-hidden border border-amber-200 bg-white hover:border-amber-400 transition"
                    title={p.name}
                    data-testid={`${testidPrefix}-thumb-${orderId}-${p.file_id}`}
                  >
                    {thumbUrls[p.file_id] ? (
                      <img
                        src={thumbUrls[p.file_id]}
                        alt={p.name || 'photo'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {state.updated_at && (
            <div
              className="text-[11px] text-amber-700 pt-1"
              data-testid={`${testidPrefix}-meta-${orderId}`}
            >
              Last updated {formatDateTime(state.updated_at)}
              {state.updated_by ? ` · by ${state.updated_by}` : ''}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx >= 0 && state.photos[lightboxIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid={`${testidPrefix}-lightbox-${orderId}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white"
            aria-label="Previous"
            data-testid={`${testidPrefix}-lightbox-prev-${orderId}`}
          >
            <ArrowLeft className="w-5 h-5 text-neutral-900" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 hover:bg-white"
            aria-label="Next"
            data-testid={`${testidPrefix}-lightbox-next-${orderId}`}
          >
            <ArrowRight className="w-5 h-5 text-neutral-900" />
          </button>
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-white"
            aria-label="Close"
            data-testid={`${testidPrefix}-lightbox-close-${orderId}`}
          >
            <X className="w-5 h-5 text-neutral-900" />
          </button>
          <img
            src={thumbUrls[state.photos[lightboxIdx].file_id] || ''}
            alt={state.photos[lightboxIdx].name || 'photo'}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PostPickupProofSection;
